import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AuthoredDesign } from "@keydials/api-client";
import { useAuthoring } from "../authoring/AuthoringProvider";
import { builderCopy as copy } from "../authoring/copy";
import { runGoLive } from "../authoring/launch";
import { runBackToEditing, testCopyOf } from "../authoring/testCopy";
import { authoringCalls } from "../authoring/useDraftSession";

/**
 * On an author's own test copy (KEY-560), under the testing notice: go live from here, or put the test copy away and go back to
 * editing. Only the author who built the dial sees it, because only their designs list names this dial as a test copy.
 */
export function TestCopyActions({ instanceId }: { instanceId: string }) {
  const authoring = useAuthoring();
  const navigate = useNavigate();
  const [design, setDesign] = useState<AuthoredDesign | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDesign(null);
    if (!authoring.offered) return;
    let cancelled = false;
    authoringCalls()
      .listMyDesigns()
      .then((designs) => {
        if (!cancelled) setDesign(testCopyOf(designs, instanceId));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authoring.offered, instanceId]);

  if (!design) return null;
  const definitionId = design.id;

  async function goLive() {
    setBusy(true);
    setError(null);
    // Nothing to save here: the builder saved everything before the test copy was made, and editing has stopped since.
    const result = await runGoLive({ flush: async () => true, freeze: () => {}, thaw: () => {}, goLive: () => authoringCalls().goLive(definitionId) });
    setBusy(false);

    switch (result.kind) {
      case "live":
        navigate(`/instances/${result.instanceId}/settings/members`);
        return;
      case "running":
        navigate(`/instances/${result.instanceId}`);
        return;
      case "notReady":
        // The checklist, with its way back to each problem, is on the builder's Launch stage.
        navigate(`/build/${definitionId}?stage=launch`);
        return;
      case "limit":
      case "refused":
        setError(result.message);
        return;
      default:
        setError(copy.launch.failed);
        return;
    }
  }

  async function backToEditing() {
    setBusy(true);
    setError(null);
    const result = await runBackToEditing({ revert: () => authoringCalls().revert(definitionId), unlock: () => {} });
    setBusy(false);

    if (result.kind === "editing") {
      navigate(`/build/${definitionId}?stage=build`);
      return;
    }
    setError(result.kind === "refused" && result.message ? result.message : copy.launch.backToEditingFailed);
  }

  return (
    <div className="kd-card kd-test-copy-actions" role="region" aria-label={copy.launch.testCopyBanner}>
      <p>
        <b>{copy.launch.testCopyBanner}</b> {copy.launch.testCopyWarning}
      </p>
      <div className="kd-launch-actions">
        <button type="button" className="kd-btn-primary" disabled={busy} onClick={() => void goLive()}>
          {copy.launch.goLive.title}
        </button>
        <button type="button" className="kd-btn" disabled={busy} onClick={() => void backToEditing()}>
          {copy.launch.backToEditing}
        </button>
      </div>
      {error && (
        <p className="kd-builder-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
